import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { publicLayoutRoute } from '@/ui/pages/public'
import { resolveLanguage } from '@/application/i18n/language'
import { CONTACT_EMAIL } from '@/application/i18n/constants'
import { useDocumentMeta } from '@/application/hooks/useDocumentMeta'
import { OG_IMAGE_URL } from '@/application/seo/site'
import { useLocalizedUrl } from '@/application/hooks/useLocalizedUrl'
import { LegalPageLayout } from '@/ui/pages/public/legal/LegalPageLayout'
import { formatLastUpdated } from '@/ui/pages/public/legal/lastUpdated'

export const mentionsLegalesRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'mentions-legales',
  component: MentionsLegalesPage,
})

/** Legal notice (LCEN art. 6-III): publisher identity, director of publication, host. */
export function MentionsLegalesPage() {
  const { t, i18n } = useTranslation()
  const lang = resolveLanguage(i18n.language)

  const pageUrl = useLocalizedUrl('/mentions-legales')

  useDocumentMeta({
    title: t('seo.mentions_legales_title'),
    description: t('seo.mentions_legales_description'),
    imageUrl: OG_IMAGE_URL,
    url: pageUrl,
    type: 'website',
  })

  return (
    <LegalPageLayout
      title={t('legal.mentions.page_title')}
      lastUpdatedLabel={formatLastUpdated(lang)}
    >
      <section>
        <h2>{t('legal.mentions.publisher_title')}</h2>
        <p>{t('legal.mentions.publisher_body')}</p>
        <p>
          <strong>{t('legal.mentions.publisher_address_label')}:</strong>{' '}
          {t('legal.mentions.publisher_address_value')}
        </p>
      </section>

      <section>
        <h2>{t('legal.mentions.director_title')}</h2>
        <p>{t('legal.mentions.director_body')}</p>
      </section>

      <section>
        <h2>{t('legal.mentions.contact_title')}</h2>
        <p>
          {t('legal.mentions.contact_body')} <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </section>

      <section>
        <h2>{t('legal.mentions.host_title')}</h2>
        <p>
          {t('legal.mentions.host_frontend_label')}: {t('legal.mentions.host_frontend_value')}
          {' — '}
          <a
            href={t('legal.mentions.host_frontend_site')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('legal.mentions.host_frontend_site')}
          </a>
        </p>
        <p>
          {t('legal.mentions.host_backend_label')}: {t('legal.mentions.host_backend_value')}
          {' — '}
          <a href={t('legal.mentions.host_backend_site')} target="_blank" rel="noopener noreferrer">
            {t('legal.mentions.host_backend_site')}
          </a>
        </p>
        <p>
          {t('legal.mentions.host_database_label')}: {t('legal.mentions.host_database_value')}
          {' — '}
          <a
            href={t('legal.mentions.host_database_site')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('legal.mentions.host_database_site')}
          </a>
        </p>
        <p>{t('legal.mentions.host_note')}</p>
      </section>
    </LegalPageLayout>
  )
}
